import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronUpDoubleIcon from "@redotech/redo-icons/chevron-up-double.svg";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import classnames from "classnames";
import Fuse from "fuse.js";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { Divider } from "./arbiter-components/divider/divider";
import { BaseRedoInput } from "./arbiter-components/input/base-redo-text-input";
import { sortColumnsByDisplayOrder } from "./column-selector-order";
import * as styles from "./column-selector.module.css";
import { Dropdown } from "./dropdown";
import { Flex } from "./flex";
import { Column2 } from "./table2";
import { Text } from "./text";
import { Tooltip } from "./tooltip/tooltip";

interface ColumnItemProps<T> {
  item: Column2<T>;
  commonProps: {
    onSelect: (selected: boolean, key: string) => void;
    onMoveToTop: (key: string) => void;
  };
  dragHandleProps: any;
  showDragHandle?: boolean;
}

export const ColumnSelector = genericMemo(function ColumnSelection<T>({
  columns,
  dropdownButtonRef,
  displayedColumns,
  setDisplayedColumns,
}: {
  columns: Column2<T>[];
  dropdownButtonRef: HTMLButtonElement | null;
  displayedColumns: { key: string; displayed: boolean }[];
  setDisplayedColumns: (value: { key: string; displayed: boolean }[]) => void;
}) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);
  const currentMouseYRef = useRef<number>(0);

  class ColumnItem extends React.Component<
    ColumnItemProps<T>,
    { hovered: boolean }
  > {
    constructor(props: ColumnItemProps<T>) {
      super(props);
      this.state = { hovered: false };
    }

    handleSelect = (selected: boolean) => {
      const { item, commonProps } = this.props;
      commonProps.onSelect(selected, item.key);
    };

    handleMoveToTop = () => {
      const { item, commonProps } = this.props;
      commonProps.onMoveToTop(item.key);
    };

    handleHover = (hovered: boolean) => {
      this.setState({ hovered });
    };

    override render() {
      const { item } = this.props;
      const { hovered } = this.state;

      const showDragHandle = this.props.showDragHandle ?? true;
      const isDisplayed =
        displayedColumns.find((col) => col.key === item.key)?.displayed ??
        false;

      return (
        <Flex
          align="center"
          className={classnames(
            styles.columnItem,
            showDragHandle && hovered ? styles.columnItemDraggable : "",
          )}
          dir="row"
          gap="xs"
          onClick={() => {
            if (!isDisplayed) {
              this.handleSelect(true);
            }
          }}
          onMouseEnter={() => this.handleHover(true)}
          onMouseLeave={() => this.handleHover(false)}
          {...(showDragHandle ? this.props.dragHandleProps : {})}
        >
          <Flex align="center" dir="row" gap="md" grow={1} shrink={1}>
            <div onClick={(e) => e.stopPropagation()}>
              <RedoCheckbox
                dataTestId={`column-selector-checkbox-${item.key}`}
                setValue={this.handleSelect}
                size="xs"
                value={isDisplayed}
              />
            </div>
            <Text fontSize="xs">{item.title}</Text>
          </Flex>
          <Flex
            align="center"
            className={styles.moveToTopButton}
            onMouseDown={(e) => e.stopPropagation()}
            shrink={0}
          >
            <Tooltip title={t("Move to top")}>
              <RedoButton
                aria-label={t("Move to top")}
                hierarchy="tertiary"
                IconLeading={ChevronUpDoubleIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleMoveToTop();
                }}
                size="xs"
              />
            </Tooltip>
          </Flex>
          {showDragHandle && (
            <div
              className={classnames(
                styles.dragHandle,
                hovered ? styles.dragHandleHovered : "",
              )}
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }}
            >
              <DotsReorderIcon className={styles.icon} />
            </div>
          )}
        </Flex>
      );
    }
  }

  const StaticColumnItem = ({ column }: { column: Column2<T> }) => {
    const isDisplayed =
      displayedColumns.find((col) => col.key === column.key)?.displayed ??
      false;

    const handleSelect = (selected: boolean) => {
      const columnIndex = displayedColumns.findIndex(
        (c) => c.key === column.key,
      );
      if (columnIndex !== -1) {
        const updatedColumns = [...displayedColumns];
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        updatedColumns[columnIndex].displayed = selected === true;
        setDisplayedColumns(updatedColumns);
      }
    };

    return (
      <Flex
        align="center"
        className={styles.columnItem}
        dir="row"
        onClick={() => {
          if (!isDisplayed) {
            handleSelect(true);
          }
        }}
      >
        <Flex align="center" dir="row" gap="md" grow={1}>
          <div onClick={(e) => e.stopPropagation()}>
            <RedoCheckbox
              dataTestId={`column-selector-checkbox-${column.key}`}
              setValue={handleSelect}
              size="xs"
              value={isDisplayed}
            />
          </div>
          <Text fontSize="xs">{column.title}</Text>
        </Flex>
        {/* Same button as the selected rows so static and draggable items
            render at identical heights. Acts as "select + move to top" — the
            shared `handleMoveToTop` handles the not-yet-in-displayedColumns
            case. */}
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Tooltip title={t("Select and move to top")}>
            <RedoButton
              aria-label={t("Select and move to top")}
              hierarchy="tertiary"
              IconLeading={ChevronUpDoubleIcon}
              onClick={(e) => {
                e.stopPropagation();
                handleMoveToTop(column.key);
              }}
              size="xs"
            />
          </Tooltip>
        </div>
      </Flex>
    );
  };

  const correctlySortedCols = useMemo(
    () =>
      sortColumnsByDisplayOrder(columns, displayedColumns).filter(
        (col) => !col.nonHidable && col.available !== false,
      ),
    [columns, displayedColumns],
  );

  const searcher = useMemo(
    () => new Fuse(correctlySortedCols, { keys: ["title"], threshold: 0.2 }),
    [correctlySortedCols],
  );

  const filteredColumns = useSearch(searcher, correctlySortedCols, searchText);

  const { selectedColumns, unselectedColumns } = useMemo(() => {
    const selected = correctlySortedCols.filter(
      (col) => displayedColumns.find((c) => c.key === col.key)?.displayed,
    );
    const unselected = correctlySortedCols
      .filter(
        (col) => !displayedColumns.find((c) => c.key === col.key)?.displayed,
      )
      .sort((a, b) => {
        const aTitle = typeof a.title === "string" ? a.title : "";
        const bTitle = typeof b.title === "string" ? b.title : "";
        return aTitle.localeCompare(bTitle);
      });
    return { selectedColumns: selected, unselectedColumns: unselected };
  }, [correctlySortedCols, displayedColumns]);

  const allSelected = useMemo(
    () =>
      correctlySortedCols.every(
        (col) => displayedColumns.find((c) => c.key === col.key)?.displayed,
      ),
    [correctlySortedCols, displayedColumns],
  );

  const handleSelectAll = useHandler(() => {
    const selectableKeys = new Set(correctlySortedCols.map((col) => col.key));
    setDisplayedColumns(
      displayedColumns.map((col) =>
        selectableKeys.has(col.key) ? { ...col, displayed: true } : col,
      ),
    );
  });

  const handleDeselectAll = useHandler(() => {
    const selectableKeys = new Set(correctlySortedCols.map((col) => col.key));
    setDisplayedColumns(
      displayedColumns.map((col) =>
        selectableKeys.has(col.key) ? { ...col, displayed: false } : col,
      ),
    );
  });

  const handleMoveToTop = useHandler((key: string) => {
    const nonHidableKeys = new Set(
      columns.filter((col) => col.nonHidable).map((col) => col.key),
    );
    const withoutNonHidable = displayedColumns.filter(
      (col) => !nonHidableKeys.has(col.key),
    );
    const idx = withoutNonHidable.findIndex((col) => col.key === key);
    // Already at top AND displayed → nothing to do. Otherwise either: this is
    // an unselected column (idx === -1) being promoted into selected+top, or a
    // selected column being moved up. Both end up as { displayed: true } at
    // the front, with everything else preserving its prior order.
    if (idx === 0 && withoutNonHidable[0]?.displayed) {
      return;
    }
    const updated =
      idx === -1
        ? [{ key, displayed: true }, ...withoutNonHidable]
        : [
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            { ...withoutNonHidable[idx]!, displayed: true },
            ...withoutNonHidable.slice(0, idx),
            ...withoutNonHidable.slice(idx + 1),
          ];
    for (const col of [...columns].reverse()) {
      if (col.nonHidable) {
        updated.unshift({ key: col.key, displayed: true });
      }
    }
    setDisplayedColumns(updated);
  });

  const toggleDropdownOpen = useHandler(() => {
    setDropdownOpen((prev) => {
      const newOpen = !prev;
      return newOpen;
    });
    setSearchText("");
  });

  useEffect(() => {
    if (!dropdownButtonRef) {
      return;
    }
    dropdownButtonRef.addEventListener("click", toggleDropdownOpen);
    return () => {
      dropdownButtonRef.removeEventListener("click", toggleDropdownOpen);
    };
  }, [dropdownButtonRef, toggleDropdownOpen]);

  const handleMouseMove = useHandler((e: MouseEvent) => {
    currentMouseYRef.current = e.clientY;

    // Only auto-scroll if the primary mouse button is pressed (dragging)
    if (e.buttons !== 1) {
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    const container = dropdownContainerRef.current;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50;
    const scrollSpeed = 10;

    const shouldScrollUp =
      mouseY < rect.top + scrollThreshold && container.scrollTop > 0;
    const shouldScrollDown =
      mouseY > rect.bottom - scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight;

    if (!shouldScrollUp && !shouldScrollDown) {
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    if (autoScrollIntervalRef.current === null) {
      const scroll = () => {
        const container = dropdownContainerRef.current;
        if (!container) {
          return;
        }

        const rect = container.getBoundingClientRect();
        const mouseY = currentMouseYRef.current;

        if (mouseY < rect.top + scrollThreshold && container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
          autoScrollIntervalRef.current = requestAnimationFrame(scroll);
        } else if (
          mouseY > rect.bottom - scrollThreshold &&
          container.scrollTop < container.scrollHeight - container.clientHeight
        ) {
          container.scrollTop += scrollSpeed;
          autoScrollIntervalRef.current = requestAnimationFrame(scroll);
        } else {
          autoScrollIntervalRef.current = null;
        }
      };

      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    }
  });

  const handleMouseUp = useHandler(() => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  });

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
      }
    };
  }, [dropdownOpen, handleMouseMove, handleMouseUp]);

  return (
    <>
      {dropdownOpen && (
        <ClickAwayListener onClickAway={toggleDropdownOpen}>
          <Dropdown
            anchor={dropdownButtonRef}
            className={styles.dropdown}
            fitToAnchor={false}
            flexProps={{ p: "none", gap: "none" }}
            open
          >
            <Flex className={styles.dropdownInner} dir="column" gap="none">
              <Flex
                align="center"
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                className={styles.filterDropdownHeader}
                dir="row"
                gap="sm"
                px="lg"
                py="md"
              >
                <Flex grow={1}>
                  <BaseRedoInput
                    autoFocus
                    placeholder={t("Search columns...")}
                    setValue={setSearchText}
                    size="sm"
                    value={searchText}
                  />
                </Flex>
                <RedoButton
                  hierarchy="link"
                  onClick={allSelected ? handleDeselectAll : handleSelectAll}
                  size="xs"
                  text={allSelected ? t("Deselect all") : t("Select all")}
                />
              </Flex>
              <Flex
                className={styles.dropdownContent}
                dir="column"
                gap="none"
                px="lg"
                py="md"
                ref={dropdownContainerRef}
              >
                {searchText.trim().length > 0 ? (
                  // It doesn't make sense to reorder columns when filtered
                  filteredColumns.length === 0 ? (
                    <Flex align="center" dir="column" justify="center" py="md">
                      <Text fontSize="xs" textColor="secondary">
                        {t("No results")}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex dir="column" gap="none">
                      {filteredColumns.map((col) => (
                        <ColumnItem
                          commonProps={{
                            onMoveToTop: handleMoveToTop,
                            onSelect: (selected: boolean, key: string) => {
                              const columnIndex = displayedColumns.findIndex(
                                (c) => c.key === key,
                              );
                              if (columnIndex !== -1) {
                                const updatedColumns = [...displayedColumns];
                                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                                updatedColumns[columnIndex].displayed =
                                  selected;
                                setDisplayedColumns(updatedColumns);
                              }
                            },
                          }}
                          dragHandleProps={{}}
                          item={col}
                          key={col.key}
                          showDragHandle={false}
                        />
                      ))}
                    </Flex>
                  )
                ) : (
                  <>
                    {selectedColumns.length > 0 && (
                      <DraggableList<
                        Column2<T>,
                        {
                          onSelect: (selected: boolean, key: string) => void;
                          onMoveToTop: (key: string) => void;
                        },
                        ColumnItem
                      >
                        commonProps={{
                          onMoveToTop: handleMoveToTop,
                          onSelect: (selected: boolean, key: string) => {
                            const columnIndex = displayedColumns.findIndex(
                              (col) => col.key === key,
                            );
                            if (columnIndex !== -1) {
                              const updatedColumns = [...displayedColumns];
                              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                              updatedColumns[columnIndex].displayed = selected;
                              setDisplayedColumns(updatedColumns);
                            }
                          },
                        }}
                        constrainDrag
                        container={() => dropdownContainerRef.current}
                        itemKey={(item: Column2<T>) => item.key}
                        list={selectedColumns}
                        onMoveEnd={(newList) => {
                          const newDisplayedColumns: {
                            key: string;
                            displayed: boolean;
                          }[] = [];
                          for (const item of newList) {
                            newDisplayedColumns.push({
                              key: item.key,
                              displayed: true,
                            });
                          }
                          for (const item of unselectedColumns) {
                            newDisplayedColumns.push({
                              key: item.key,
                              displayed: false,
                            });
                          }

                          for (const col of columns.reverse()) {
                            if (col.nonHidable) {
                              newDisplayedColumns.unshift({
                                key: col.key,
                                displayed: true,
                              });
                            }
                          }

                          setDisplayedColumns([...newDisplayedColumns]);
                        }}
                        padding={0}
                        template={ColumnItem}
                      />
                    )}

                    {selectedColumns.length > 0 &&
                      unselectedColumns.length > 0 && (
                        <Divider classes={styles.sectionDivider} />
                      )}

                    {unselectedColumns.length > 0 && (
                      <Flex dir="column" gap="none">
                        {unselectedColumns.map((col) => (
                          <StaticColumnItem column={col} key={col.key} />
                        ))}
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
});
